import type { UnifiedEvent } from "@/lib/types";
import { parseRealtimeEnvelope } from "@/lib/telemetry";

export type LiveSocketCallbacks = {
  onEvents: (events: UnifiedEvent[]) => void;
  onStatus: (status: string) => void;
  onError: (error: string) => void;
};

export type LiveSocketHandle = {
  close: () => void;
};

const buildWsUrl = (telemetryOrigin: string, org: string, token: string | null): string => {
  const url = new URL(telemetryOrigin);
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const wsUrl = new URL(`${protocol}//${url.host}/${encodeURIComponent(org)}/ws`);
  if (token) {
    wsUrl.searchParams.set("token", token);
  }
  return wsUrl.toString();
};

export const connectLiveTopic = (
  input: {
    telemetryOrigin: string;
    org: string;
    topic: "logs" | "traces";
    authToken: string | null;
  },
  callbacks: LiveSocketCallbacks,
): LiveSocketHandle => {
  let closed = false;
  let socket: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let attempt = 0;

  const clearReconnect = () => {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (closed) return;
    clearReconnect();
    attempt += 1;
    const delay = Math.min(10_000, 500 * 2 ** Math.min(5, attempt));
    callbacks.onStatus(`reconnecting ${input.topic} in ${Math.round(delay / 1000)}s`);
    reconnectTimer = window.setTimeout(() => {
      connect();
    }, delay);
  };

  const connect = () => {
    clearReconnect();
    const wsUrl = buildWsUrl(input.telemetryOrigin, input.org, input.authToken);
    callbacks.onStatus(`connecting ${input.topic}…`);
    socket = new WebSocket(wsUrl);

    socket.addEventListener("open", () => {
      attempt = 0;
      callbacks.onStatus(`live ${input.topic} connected`);
      const clientId = `logs-ui-${input.topic}-${crypto.randomUUID()}`;
      socket?.send(
        JSON.stringify({
          clientId,
          action: "subscribe",
          topic: input.topic,
          ...(input.authToken ? { apiToken: input.authToken } : {}),
        }),
      );
    });

    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      try {
        const payload = JSON.parse(event.data) as {
          topic?: string;
          value?: unknown;
          type?: string;
          message?: string;
        };
        if (payload.type === "subscription_confirmed") return;
        if (!payload.topic) return;
        const events = parseRealtimeEnvelope(payload.topic, payload.value);
        if (events.length > 0) {
          callbacks.onEvents(events);
        }
      } catch (error) {
        callbacks.onError(error instanceof Error ? error.message : String(error));
      }
    });

    socket.addEventListener("error", () => {
      callbacks.onError(`socket error (${input.topic})`);
    });

    socket.addEventListener("close", () => {
      socket = null;
      if (!closed) {
        scheduleReconnect();
      }
    });
  };

  connect();

  return {
    close: () => {
      closed = true;
      clearReconnect();
      if (socket) {
        socket.close(1000, "manual close");
        socket = null;
      }
    },
  };
};
